import { retreatSlug, resolveRetreatOffer } from "./retreatSlug";

const DEFAULT_BASE_URL = "https://yogaretreatsbooking.com";

function getRetreatImageSrc(retreat) {
  if (retreat?.source === "realhappiness") return "/images/realhappiness.jpg";
  if (retreat?.source === "patanjaliyoga") return "/images/patanjali_yoga_foundation.jpg";
  if (retreat?.thumbnail_url) {
    const raw = String(retreat.thumbnail_url).trim().replace(/^\/+/, "");
    const p = raw.startsWith("images/") ? raw : `images/${raw}`;
    return `/${p}`;
  }
  return "/images/nalanda.jpg";
}

function buildRetreatDescription(retreat, pageTitle) {
  const where = retreat.location ? retreat.location.split(",")[0].trim() : "India";
  const lead = `${pageTitle} in ${where}.`;
  const body = retreat.description
    ? ` ${retreat.description.replace(/\s+/g, " ").trim()}`
    : " Compare dates, prices, meals, ratings and reviews on Yoga Retreats Booking.";
  return lead + body;
}

function formatSourceBrand(source) {
  const raw = String(source || "").trim();
  if (!raw) return null;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function hasAggregateRating(retreat) {
  return (
    retreat?.google_rating != null &&
    retreat?.google_review_count != null &&
    Number(retreat.google_review_count) > 0
  );
}

function buildAggregateRating(retreat) {
  return {
    "@type": "AggregateRating",
    ratingValue: String(retreat.google_rating),
    reviewCount: String(retreat.google_review_count),
    bestRating: "5",
    worstRating: "1",
  };
}

function buildProductOffer(retreat, pageUrl, offer) {
  if (!offer) return null;
  return {
    "@type": "Offer",
    price: offer.price,
    priceCurrency: offer.priceCurrency,
    availability: "https://schema.org/InStock",
    url: retreat.source_url || pageUrl,
  };
}

function buildStructuredDataNode(retreat, { pageTitle, pageDescription, pageUrl, absoluteImage, offer }) {
  const rating = hasAggregateRating(retreat);
  const brandName = formatSourceBrand(retreat.source);
  const offers = buildProductOffer(retreat, pageUrl, offer);

  if (offers || rating) {
    return {
      "@type": "Product",
      name: pageTitle,
      description: pageDescription,
      image: absoluteImage,
      url: pageUrl,
      ...(brandName ? { brand: { "@type": "Brand", name: brandName } } : {}),
      ...(rating ? { aggregateRating: buildAggregateRating(retreat) } : {}),
      ...(offers ? { offers } : {}),
    };
  }

  const where = retreat.location ? retreat.location.split(",")[0].trim() : "India";
  return {
    "@type": "TouristTrip",
    name: pageTitle,
    description: pageDescription,
    url: pageUrl,
    image: absoluteImage,
    touristType: "Yoga retreat",
    itinerary: {
      "@type": "Place",
      name: where,
      ...(retreat.location ? { address: retreat.location } : {}),
    },
  };
}

/** Structured data for a retreat detail page. */
export function buildRetreatJsonLd(retreat, { baseUrl = DEFAULT_BASE_URL, canonicalPath, imageSrc } = {}) {
  const pageTitle = retreat.retreat_name || "Yoga Retreat";
  const pageDescription = buildRetreatDescription(retreat, pageTitle);
  const path = canonicalPath || `/retreats/${retreatSlug(retreat)}`;
  const pageUrl = `${baseUrl}${path}`;
  const image = imageSrc || getRetreatImageSrc(retreat);
  const absoluteImage = image.startsWith("/") ? `${baseUrl}${image}` : image;
  const offer = resolveRetreatOffer(retreat);

  const locationSlug = retreat.location_tags
    ? retreat.location_tags.split(",")[0].trim().toLowerCase().replace(/\s+/g, "-")
    : null;

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${baseUrl}/` },
          {
            "@type": "ListItem",
            position: 2,
            name: retreat.location ? `Yoga Retreats in ${retreat.location.split(",")[0].trim()}` : "Retreats",
            item: locationSlug ? `${baseUrl}/yoga-retreats-in/${locationSlug}` : `${baseUrl}/`,
          },
          { "@type": "ListItem", position: 3, name: pageTitle, item: pageUrl },
        ],
      },
      buildStructuredDataNode(retreat, { pageTitle, pageDescription, pageUrl, absoluteImage, offer }),
    ],
  };
}
